import React, { useEffect } from 'react';
import Hero from '../components/Hero';
import { heroImages } from '../utils/heroImages';
import { Youtube, Users, Trophy, Newspaper, ArrowRight } from 'lucide-react';

const AboutPage: React.FC = () => {
  useEffect(() => {
    document.title = 'About Us | CryptoIndia';
  }, []);

  const stats = [
    {
      icon: <Youtube className="w-8 h-8 text-yellow-400" />,
      value: "350K+",
      label: "YouTube Subscribers"
    },
    {
      icon: <Users className="w-8 h-8 text-yellow-400" />,
      value: "68K+",
      label: "Telegram Members"
    },
    {
      icon: <Trophy className="w-8 h-8 text-yellow-400" />,
      value: "1M+",
      label: "Monthly Views"
    },
    {
      icon: <Newspaper className="w-8 h-8 text-yellow-400" />,
      value: "8-12",
      label: "Videos Per Month"
    }
  ];

  return (
    <div className="bg-black">
      <Hero 
        title="About CryptoIndia"
        subtitle="India's largest crypto YouTube channel — 350K subscribers, 8-12 videos/month, 68K+ community"
        config={heroImages.about}
      />

      {/* Main Content */}
      <section className="py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg prose-invert">
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                CryptoIndia is India's largest crypto YouTube channel with 350K subscribers. We make crypto simple, accessible and profitable for millions of Indian investors through daily videos, real-time market analysis and community education.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a 
                  href="https://www.youtube.com/@cryptoindia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn bg-yellow-400 hover:bg-yellow-500 text-black group flex-1 justify-center"
                >
                  Subscribe on YouTube 
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
                <a 
                  href="#newsletter"
                  className="btn border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black group flex-1 justify-center"
                >
                  Join Newsletter 
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-zinc-900">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">Our Mission</h2>
            <div className="space-y-6 text-gray-300">
              <p>
                To be India's most trusted crypto voice — through honest education, real-time market insights, 
                and a community that helps each other navigate crypto profitably.
              </p>
              <p>
                We publish 8-12 videos every month, run India's most active crypto Telegram community, 
                and partner only with the exchanges and projects we believe in.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section id="newsletter" className="py-16 bg-zinc-900">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Stay Updated</h2>
            <p className="text-gray-300 mb-8">
              Join our newsletter for exclusive content, market insights, and the latest crypto news.
            </p>
            <form className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-6 py-3 rounded-lg bg-black text-white border border-zinc-800 focus:border-yellow-400 focus:outline-none"
                required
              />
              <div className="cf-turnstile" data-sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'} data-callback="onTurnstileSuccess" data-theme="dark"></div>
              <button 
                type="submit"
                className="btn bg-yellow-400 hover:bg-yellow-500 text-black whitespace-nowrap"
              >
                Subscribe Now
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Sponsorship CTA */}
      <section className="py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Work With Us</h2>
            <p className="text-gray-300 mb-8">
              Interested in partnering with India's leading crypto media platform? 
              Let's explore how we can work together.
            </p>
            <a 
              href="mailto:anant@cryptoindia.io"
              className="btn bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800 group inline-flex"
            >
              Learn About Sponsorships 
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;